import { List, Show, Create, Edit, Datagrid, TextField, ChipField, DateField, SimpleForm, TextInput, SelectInput, Button, Filter, SearchInput, BooleanField, useListContext, useGetList } from 'react-admin';
import { Box, Typography, Paper, Grid, Alert, Chip, Card, CardContent, LinearProgress, Stepper, Step, StepLabel } from '@mui/material';
import { Link } from 'react-router-dom';
import { Person, Security, CheckCircle, Warning, Shield, Group, VerifiedUser, AdminPanelSettings, ArrowForward } from '@mui/icons-material';
import { PieChart, BarChart } from '@mui/x-charts-pro';
import { ListPageHeader } from '@/components/ListPageHeader';

interface AdminUser {
  status?: string;
  roles?: string[];
  [key: string]: unknown;
}

interface AdminRole {
  status?: string;
  [key: string]: unknown;
}

const USER_STATUS = ['Active', 'Suspended', 'Locked', 'Deactivated'];
const USER_ROLES = ['Platform Admin', 'HR Manager', 'HR Officer', 'Employee', 'Manager', 'Compliance Officer', 'Finance', 'OHS Officer'];
const ROLE_STATUS = ['Active', 'Inactive'];
const PERMISSION_GROUPS = ['Employees', 'Onboarding', 'Skills Development', 'Industrial Relations', 'Employment Equity', 'Offboarding', 'OHS', 'Billing', 'Admin', 'Reports', 'Compliance', 'Dashboard', 'Audit'];

export const UserList = () => (
  <List filters={<UserFilters />}>
    <UserListHeader />
    <Datagrid rowClick="show">
      <TextField source="name" />
      <TextField source="email" />
      <ChipField source="role" label="Role" />
      <ChipField source="status" />
      <TextField source="tenant" label="Tenant" />
      <BooleanField source="mfaEnabled" label="MFA" />
    </Datagrid>
  </List>
);

const UserListHeader = () => {
  const { data: users } = useListContext();
  const total = users?.length ?? 0;
  const active = users?.filter((u: AdminUser) => u.status === 'Active').length ?? 0;
  const suspended = users?.filter((u: AdminUser) => u.status === 'Suspended').length ?? 0;
  const deactivated = users?.filter((u: AdminUser) => u.status === 'Deactivated').length ?? 0;

  return (
    <ListPageHeader
      title="User Management"
      metrics={[
        { label: 'Active Users', value: String(active), color: 'success.main' },
        { label: 'Suspended', value: String(suspended), color: 'warning.main' },
        { label: 'Deactivated', value: String(deactivated), color: 'error.main' },
      ]}
      pieChartData={[
        { label: 'Active', value: active },
        { label: 'Suspended', value: suspended },
        { label: 'Deactivated', value: deactivated },
      ].filter(d => d.value > 0)}
      pieChartTitle="User Status"
    />
  );
};

export const UserFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search by name or email" />
    <SelectInput source="status" choices={USER_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
    <SelectInput source="role" choices={USER_ROLES.map(r => ({ id: r, name: r }))} />
  </Filter>
);

export const UserShow = () => (
  <Show render={({ record }) => {
    if (!record) return null;
    const isSuspended = record.status === 'Suspended';
    const isDeactivated = record.status === 'Deactivated';
    const isLocked = record.status === 'Locked';

    return (
      <Box sx={{ p: 3 }}>
        {isSuspended && <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>User account suspended — contact administrator</Alert>}
        {isDeactivated && <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>User deactivated — login disabled</Alert>}
        {isLocked && <Alert severity="error" sx={{ mb: 2 }} icon={<Security />}>Account locked due to failed login attempts</Alert>}
        {record.status === 'Active' && <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>User active and authorized</Alert>}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Name', value: record.name, bold: true },
            { label: 'Email', value: record.email },
            { label: 'Role', value: record.role, chip: true },
            { label: 'Status', value: record.status, chip: true },
            { label: 'Tenant', value: record.tenant },
            { label: 'MFA Enabled', value: record.mfaEnabled ? 'Yes' : 'No' },
            { label: 'Legal Entity', value: record.legalEntity || '—' },
            { label: 'Last Login', value: record.lastLogin || 'Never' },
          ].map(f => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                {f.chip ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={f.value} size="small" color={record.status === 'Active' ? 'success' : record.status === 'Suspended' ? 'warning' : 'error'} />
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>User Permissions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {PERMISSION_GROUPS.slice(0, 6).map(group => (
                  <Box key={group} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle color="success" fontSize="small" />
                    <Typography variant="body2">{group}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Quick Actions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button component={Link} to="/audit-logs" variant="outlined" size="small" startIcon={<Security />}>
                  View Audit Logs
                </Button>
                <Button component={Link} to="/roles" variant="outlined" size="small" startIcon={<Shield />}>
                  Manage Roles
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button component={Link} to="/notifications" variant="outlined" size="small" startIcon={<AdminPanelSettings />}>
            Send Notification
          </Button>
        </Box>
      </Box>
    );
  }} />
);

export const UserCreate = () => (
  <Create>
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 2 }}>
        User will be provisioned in Keycloak — email must be unique across the platform
      </Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="name" label="Full Name" fullWidth />
        <TextInput source="email" type="email" fullWidth />
        <SelectInput source="role" choices={USER_ROLES.map(r => ({ id: r, name: r }))} fullWidth />
        <SelectInput source="tenant" choices={[{ id: 'ACME001', name: 'ACME001' }, { id: 'TECH002', name: 'TECH002' }]} fullWidth label="Tenant" />
        <SelectInput source="legalEntity" choices={[{ id: 'ACME-HQ', name: 'ACME HQ' }, { id: 'TECH-JHB', name: 'TECH Johannesburg' }]} fullWidth label="Legal Entity" />
        <SelectInput source="status" choices={USER_STATUS.map(s => ({ id: s, name: s }))} fullWidth defaultValue="Active" />
      </Box>
    </SimpleForm>
  </Create>
);

export const UserEdit = () => (
  <Edit>
    <SimpleForm>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Role changes take effect on next login — active sessions are not affected
      </Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="name" label="Full Name" fullWidth />
        <TextInput source="email" type="email" fullWidth />
        <SelectInput source="role" choices={USER_ROLES.map(r => ({ id: r, name: r }))} fullWidth />
        <SelectInput source="status" choices={USER_STATUS.map(s => ({ id: s, name: s }))} fullWidth />
        <SelectInput source="mfaEnabled" choices={[{ id: 'true', name: 'Enabled' }, { id: 'false', name: 'Disabled' }]} fullWidth label="MFA" />
      </Box>
    </SimpleForm>
  </Edit>
);

export const RoleList = () => (
  <List filters={<RoleFilters />}>
    <RoleListHeader />
    <Datagrid rowClick="show">
      <TextField source="name" />
      <TextField source="description" />
      <ChipField source="status" />
      <TextField source="userCount" label="Users" />
      <TextField source="permissionCount" label="Permissions" />
    </Datagrid>
  </List>
);

const RoleListHeader = () => {
  const { data: roles } = useListContext();
  const { data: users } = useGetList('users', { pagination: { page: 1, perPage: 1000 } });
  const total = roles?.length ?? 0;
  const active = roles?.filter((r: AdminRole) => r.status === 'Active').length ?? 0;
  const inactive = roles?.filter((r: AdminRole) => r.status === 'Inactive').length ?? 0;

  const roleCounts: Record<string, number> = {};
  users?.forEach((u: AdminUser) => {
    const roleName = u.roles?.[0] || 'Unknown';
    roleCounts[roleName] = (roleCounts[roleName] || 0) + 1;
  });
  const roleBreakdown = Object.entries(roleCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);

  return (
    <ListPageHeader
      title="Role Management"
      metrics={[
        { label: 'Total Roles', value: String(total), color: 'primary.main' },
        { label: 'Active', value: String(active), color: 'success.main' },
        { label: 'Inactive', value: String(inactive), color: 'default' },
      ]}
      pieChartData={roleBreakdown}
      pieChartTitle="Users by Role"
    />
  );
};

export const RoleFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search role name" />
    <SelectInput source="status" choices={ROLE_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
  </Filter>
);

export const RoleShow = () => (
  <Show render={({ record }) => {
    if (!record) return null;
    const isInactive = record.status === 'Inactive';

    return (
      <Box sx={{ p: 3 }}>
        {isInactive && <Alert severity="info" sx={{ mb: 2 }}>Role inactive — not assignable to new users</Alert>}
        {record.status === 'Active' && <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>Role active and assignable</Alert>}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Role Name', value: record.name, bold: true },
            { label: 'Description', value: record.description },
            { label: 'Status', value: record.status, chip: true },
            { label: 'Users', value: record.userCount },
            { label: 'Permissions', value: record.permissionCount },
          ].map(f => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                {f.chip ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={f.value} size="small" color={record.status === 'Active' ? 'success' : 'default'} />
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Permissions by Group</Typography>
              <Grid container spacing={2}>
                {PERMISSION_GROUPS.map(group => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={group}>
                    <Card variant="outlined" sx={{ borderLeft: '3px solid', borderColor: 'primary.main' }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Security fontSize="small" color="action" />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{group}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {['read', 'write'].map(perm => (
                            <Box key={perm} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckCircle color="success" fontSize="small" sx={{ fontSize: 14 }} />
                              <Typography variant="caption">{group.toLowerCase().replace(' ', ':')}:{perm}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button component={Link} to="/users" variant="outlined" size="small" startIcon={<Group />}>
            View Users
          </Button>
          <Button component={Link} to="/audit-logs" variant="outlined" size="small" startIcon={<Security />}>
            View Audit Logs
          </Button>
        </Box>
      </Box>
    );
  }} />
);

export const RoleCreate = () => (
  <Create>
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 2 }}>
        Permissions follow the pattern: module:read, module:write per SABS RBAC model
      </Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="name" label="Role Name" fullWidth helperText="Min 3, max 50 characters" />
        <TextInput source="description" multiline fullWidth helperText="Min 10, max 200 characters" />
        <SelectInput source="status" choices={ROLE_STATUS.map(s => ({ id: s, name: s }))} fullWidth defaultValue="Active" />
      </Box>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Permission Groups</Typography>
      <Grid container spacing={1}>
        {PERMISSION_GROUPS.map(group => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={group}>
            <Chip label={group} icon={<Security fontSize="small" />} variant="outlined" />
          </Grid>
        ))}
      </Grid>
    </SimpleForm>
  </Create>
);

export const RoleEdit = () => (
  <Edit>
    <SimpleForm>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="name" label="Role Name" fullWidth />
        <TextInput source="description" multiline fullWidth />
        <SelectInput source="status" choices={ROLE_STATUS.map(s => ({ id: s, name: s }))} fullWidth />
      </Box>
    </SimpleForm>
  </Edit>
);

export const NotificationList = () => (
  <List filters={<NotificationFilters />}>
    <Datagrid rowClick="show">
      <TextField source="ref" label="Ref" />
      <TextField source="recipientName" label="Recipient" />
      <ChipField source="channel" label="Channel" />
      <TextField source="template" label="Template" />
      <ChipField source="status" />
      <TextField source="sentAt" label="Sent At" />
    </Datagrid>
  </List>
);

export const NotificationFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search by reference or recipient" />
    <SelectInput source="channel" choices={['WhatsApp', 'SMS', 'Email'].map(c => ({ id: c, name: c }))} />
    <SelectInput source="status" choices={['Pending', 'Sent', 'Delivered', 'Failed', 'Bounced'].map(s => ({ id: s, name: s }))} alwaysOn />
  </Filter>
);

export const NotificationShow = () => (
  <Show render={({ record }) => {
    if (!record) return null;
    const isFailed = record.status === 'Failed';
    const isDelivered = record.status === 'Delivered';
    const isPending = record.status === 'Pending';

    return (
      <Box sx={{ p: 3 }}>
        {isFailed && <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>Notification failed — check provider logs</Alert>}
        {isDelivered && <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>Notification delivered successfully</Alert>}
        {isPending && <Alert severity="info" sx={{ mb: 2 }}>Notification pending delivery</Alert>}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Reference', value: record.ref, bold: true },
            { label: 'Recipient', value: record.recipientName },
            { label: 'Channel', value: record.channel, chip: true },
            { label: 'Template', value: record.template },
            { label: 'Status', value: record.status, chip: true },
            { label: 'Sent At', value: record.sentAt || '—' },
            { label: 'Delivered At', value: record.deliveredAt || '—' },
            { label: 'Provider ID', value: record.providerMessageId || '—' },
          ].map(f => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                {f.chip ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={f.value} size="small" color={record.status === 'Delivered' ? 'success' : record.status === 'Failed' ? 'error' : 'default'} />
                  </Box>
                ) : (
                  <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Notification Process</Typography>
              <Stepper activeStep={['Pending', 'Sent', 'Delivered', 'Failed'].indexOf(record.status || 'Pending')} alternativeLabel>
                <Step completed={['Sent', 'Delivered'].includes(record.status)}>
                  <StepLabel>Created</StepLabel>
                </Step>
                <Step completed={['Delivered'].includes(record.status)}>
                  <StepLabel>Sent</StepLabel>
                </Step>
                <Step completed={record.status === 'Delivered'}>
                  <StepLabel>Delivered</StepLabel>
                </Step>
              </Stepper>
            </Paper>
          </Grid>
        </Grid>

        {record.content && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">Message Content</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{record.content}</Typography>
          </Paper>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button component={Link} to="/users" variant="outlined" size="small" startIcon={<Person />}>
            View Users
          </Button>
          <Button component={Link} to="/audit-logs" variant="outlined" size="small" startIcon={<Security />}>
            View Audit Logs
          </Button>
        </Box>
      </Box>
    );
  }} />
);

export const NotificationCreate = () => (
  <Create>
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 2 }}>
        Notifications are typically triggered by platform events — manual dispatch is for testing
      </Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="ref" label="Reference" fullWidth />
        <TextInput source="recipientName" label="Recipient Name" fullWidth />
        <SelectInput source="channel" choices={['WhatsApp', 'SMS', 'Email'].map(c => ({ id: c, name: c }))} fullWidth />
        <SelectInput source="template" choices={['invoice-issued', 'probation-reminder', 'training-scheduled', 'disciplinary-warning', 'ohs-certificate-expiry', 'termination-notice', 'welcome'].map(t => ({ id: t, name: t }))} fullWidth label="Template" />
        <SelectInput source="status" choices={['Pending', 'Sent', 'Delivered', 'Failed'].map(s => ({ id: s, name: s }))} fullWidth defaultValue="Pending" />
      </Box>
      <TextInput source="content" multiline fullWidth label="Message Content" />
    </SimpleForm>
  </Create>
);

export const NotificationEdit = () => (
  <Edit>
    <SimpleForm>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="ref" label="Reference" fullWidth />
        <TextInput source="recipientName" label="Recipient Name" fullWidth />
        <SelectInput source="channel" choices={['WhatsApp', 'SMS', 'Email'].map(c => ({ id: c, name: c }))} fullWidth />
        <SelectInput source="template" choices={['invoice-issued', 'probation-reminder', 'training-scheduled', 'disciplinary-warning', 'ohs-certificate-expiry', 'termination-notice', 'welcome'].map(t => ({ id: t, name: t }))} fullWidth label="Template" />
        <SelectInput source="status" choices={['Pending', 'Sent', 'Delivered', 'Failed'].map(s => ({ id: s, name: s }))} fullWidth />
        <DateField source="sentAt" label="Sent At" />
        <DateField source="deliveredAt" label="Delivered At" />
      </Box>
      <TextInput source="content" multiline fullWidth label="Message Content" />
    </SimpleForm>
  </Edit>
);

export const UserDashboard = () => {
  const { data: users } = useGetList('users', { pagination: { page: 1, perPage: 1000 } });
  const { data: roles } = useGetList('roles', { pagination: { page: 1, perPage: 100 } });

  const active = users?.filter((u: AdminUser) => u.status === 'Active').length ?? 0;
  const suspended = users?.filter((u: AdminUser) => u.status === 'Suspended').length ?? 0;
  const deactivated = users?.filter((u: AdminUser) => u.status === 'Deactivated').length ?? 0;

  const roleCounts: Record<string, number> = {};
  users?.forEach((u: AdminUser) => {
    const roleName = u.roles?.[0] || 'Unknown';
    roleCounts[roleName] = (roleCounts[roleName] || 0) + 1;
  });
  const roleLabels = Object.keys(roleCounts).slice(0, 6);
  const roleData = roleLabels.map(r => roleCounts[r] ?? 0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>User & Access Management</Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Active Users', value: String(active), color: 'success.main' },
          { label: 'Suspended', value: String(suspended), color: 'warning.main' },
          { label: 'Deactivated', value: String(deactivated), color: 'error.main' },
          { label: 'Roles Defined', value: String(roles?.length ?? 0), color: 'primary.main' },
        ].map(stat => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={stat.label}>
            <Paper sx={{ p: 3, borderTop: '4px solid', borderColor: stat.color }}>
              <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
              <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 700 }}>{stat.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Users by Role</Typography>
            <Box sx={{ height: 200 }}>
              <BarChart xAxis={[{ scaleType: 'band', data: roleLabels }]} series={[{ data: roleData }]} height={190} />
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>User Status Distribution</Typography>
            <Box sx={{ height: 200 }}>
              <PieChart series={[
                { data: [
                  { label: 'Active', value: active },
                  { label: 'Suspended', value: suspended },
                  { label: 'Deactivated', value: deactivated },
                ].filter(d => d.value > 0), innerRadius: 30, outerRadius: 70 },
              ]} height={190} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Button component={Link} to="/users" variant="outlined" size="small">
          View All Users
        </Button>
      </Box>
    </Box>
  );
};